import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../Login.css';
import logo from '../../assets/img.png';
import axiosInstance from "../../api/axiosConfig.jsx";

const RegisterAccount = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [isValidToken, setIsValidToken] = useState(null);
    const [formData, setFormData] = useState({
        userName: '',
        password: '',
        email: '',
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [language, setLanguage] = useState('nl');

    const toggleLanguage = () => {
        setLanguage((prevLanguage) => (prevLanguage === 'nl' ? 'en' : 'nl'));
    };

    // Foutmeldingen in verschillende talen
    const errorMessages = {
        nl: {
            emailInUse: 'Dit e-mailadres is al in gebruik.',
            usernameInUse: 'Deze gebruikersnaam is al in gebruik.',
            registrationError: 'Fout bij het registreren van de gebruiker.',
            invalidToken: 'Ongeldige of verlopen link.',
        },
        en: {
            emailInUse: 'This email address is already in use.',
            usernameInUse: 'Username is already taken.',
            registrationError: 'Error registering user.',
            invalidToken: 'Invalid or expired link.',
        },
    };

    // Validate token when the page loads
    useEffect(() => {
        const validateToken = async () => {
            try {
                const response = await axiosInstance.get(`/users/validate-link/${token}`);
                if (response.status === 200) {
                    setIsValidToken(true);
                }
            } catch (error) {
                setIsValidToken(false);
            }
        };
        validateToken();
    }, [token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            const response = await axiosInstance.post(`/users/register/${token}`, formData);
            if (response.status === 200) {
                setSuccess(language === 'nl' ? 'Registratie succesvol!' : 'Registration successful!');
                setTimeout(() => {
                    navigate('/login');
                }, 3000); // Redirect after 3 seconds
            }
        } catch (error) {
            console.log(error);
            // Check if the error response exists and set the error message accordingly
            if (error.response && error.response.data) {
                // Translate the error message based on the current language
                const translatedMessage = translateErrorMessage(error.response.data);
                setError(translatedMessage);
            } else {
                setError(language === 'nl' ? errorMessages.nl.registrationError : errorMessages.en.registrationError);
            }
        }
    };

    // Translate error messages based on the current language
    const translateErrorMessage = (message) => {
        switch (message) {
            case 'EMAIL_IN_USE':
                return errorMessages[language].emailInUse;
            case 'USERNAME_IN_USE':
                return errorMessages[language].usernameInUse;
            case 'FAILED_TO_REGISTER_USER':
                return errorMessages[language].registrationError;
            default:
                return language === 'nl' ? 'Onbekende fout opgetreden.' : 'An unknown error occurred.';
        }
    };

    // Check if token is valid
    if (isValidToken === false) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger text-center">
                    <h4 className="alert-heading">{language === 'nl' ? 'Ongeldige of Verlopen Link' : 'Invalid or Expired Link'}</h4>
                    <p>{language === 'nl' ? 'Deze registratie link is ongeldig of is verlopen. Neem contact op met de ondersteuning.' : 'This registration link is either invalid or has expired. Please contact support.'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid vh-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', minWidth: '100vw' }}>
            <div className="card shadow-lg p-4" style={{ maxWidth: '400px', width: '100%', borderRadius: '15px', backgroundColor: '#ffffff', maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="text-center mb-4">
                    <img src={logo} alt="HorecaMasters Logo" style={{ maxWidth: '250px', height: '130px' }} />
                </div>
                <h2 className="text-center mb-4" style={{ fontSize: '1.5rem', color: '#343a40' }}>
                    {language === 'nl' ? 'Registratie' : 'Registration'}
                </h2>

                {error && <div className="alert alert-danger">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label htmlFor="userName" className="form-label">{language === 'nl' ? 'Gebruikersnaam' : 'User Name'}</label>
                        <input
                            type="text"
                            className="form-control"
                            id="userName"
                            name="userName"
                            value={formData.userName}
                            onChange={handleChange}
                            placeholder={language === 'nl' ? 'Voer je gebruikersnaam in' : 'Enter your user name'}
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="password" className="form-label">{language === 'nl' ? 'Wachtwoord' : 'Password'}</label>
                        <input
                            type="password"
                            className="form-control"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder={language === 'nl' ? 'Voer je wachtwoord in' : 'Enter your password'}
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="email" className="form-label">{language === 'nl' ? 'E-mail' : 'Email'}</label>
                        <input
                            type="email"
                            className="form-control"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={language === 'nl' ? 'Voer je e-mailadres in' : 'Enter your email'}
                            required
                        />
                    </div>
                    <button type="submit" className="btn btn-success w-100">{language === 'nl' ? 'Registreer' : 'Register'}</button>
                    <div className="form-check form-switch mt-3 text-center">
                        <input className="form-check-input" type="checkbox" id="languageSwitch" onChange={toggleLanguage} checked={language === 'en'} />
                        <label className="form-check-label" htmlFor="languageSwitch">
                            {language === 'nl' ? 'Schakel naar Engels' : 'Switch to Dutch'}
                        </label>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RegisterAccount;
